import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HotelReservation, useAddTripItem, TripData } from "@/hooks/useTrips";
import { useAutoEnrichHotel } from "@/hooks/useHotelEnrichment";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const hotelSchema = z.object({
  hotel_name: z.string().min(1, "Hotel name is required"),
  address: z.string().optional(),
  city: z.string().min(1, "City is required"),
  check_in_date: z.string().min(1, "Check-in date is required"),
  check_out_date: z.string().min(1, "Check-out date is required"),
  room_type: z.string().optional(),
  confirmation_number: z.string().optional(),
});

type HotelFormData = z.infer<typeof hotelSchema>;

interface AddHotelDialogProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddHotelDialog = ({
  tripId,
  open,
  onOpenChange,
}: AddHotelDialogProps) => {
  const addItem = useAddTripItem();
  const autoEnrich = useAutoEnrichHotel();

  const form = useForm<HotelFormData>({
    resolver: zodResolver(hotelSchema),
    defaultValues: {
      hotel_name: "",
      address: "",
      city: "",
      check_in_date: "",
      check_out_date: "",
      room_type: "",
      confirmation_number: "",
    },
  });

  const onSubmit = (data: HotelFormData) => {
    addItem.mutate(
      {
        tripId,
        itemType: "hotels",
        item: data as HotelReservation,
      },
      {
        onSuccess: async () => {
          toast.success("Hotel added");
          form.reset();
          onOpenChange(false);

          // Get the index of the newly added hotel and auto-enrich
          try {
            const { data: trip } = await supabase
              .from("trips")
              .select("trip_data")
              .eq("id", tripId)
              .single();

            if (trip?.trip_data) {
              const tripData = trip.trip_data as TripData;
              const hotels = tripData.hotels || [];
              const newHotelIndex = hotels.length - 1;

              if (newHotelIndex >= 0) {
                autoEnrich.mutate({
                  tripId,
                  hotelIndex: newHotelIndex,
                  hotel: hotels[newHotelIndex],
                });
              }
            }
          } catch (err) {
            console.error("Failed to auto-enrich:", err);
          }
        },
        onError: (error) => {
          toast.error(`Failed to add: ${error.message}`);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Hotel</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="hotel_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hotel Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Marriott Downtown" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input placeholder="123 Main St" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="New York" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="check_in_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Check-in Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="check_out_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Check-out Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="room_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Room Type</FormLabel>
                    <FormControl>
                      <Input placeholder="King Suite" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmation_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmation #</FormLabel>
                    <FormControl>
                      <Input placeholder="ABC123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addItem.isPending}>
                {addItem.isPending ? "Adding..." : "Add Hotel"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
